import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { AppUser } from '@/context/AuthContext';

export const fetchUserProfile = async (user: User): Promise<{ data: AppUser | null, error: string | null }> => {
  try {
    const { data: profile, error } = await supabase
      .rpc('get_profile_by_id', { user_id: user.id });
    
    if (error) {
      throw error;
    }
    
    if (!profile || !profile[0]) {
      throw new Error('Profile not found');
    }
    
    // Create an enhanced user with profile data
    const enhancedUser: AppUser = {
      ...user,
      username: profile[0].username || user.user_metadata?.username || user.email?.split('@')[0] || 'user',
      isPro: profile[0].is_pro,
      isAdmin: profile[0].is_admin,
      proExpiresAt: profile[0].pro_expires_at || null
    };
    
    return { data: enhancedUser, error: null };
  } catch (error: any) {
    // Return a minimal profile to prevent UI from breaking
    const fallbackUser: AppUser = {
      ...user,
      username: user.user_metadata?.username || user.email?.split('@')[0] || 'user',
      isPro: false,
      isAdmin: false,
      proExpiresAt: null
    };
    
    return { data: fallbackUser, error: error.message };
  }
};

import { SUPABASE_URL, SUPABASE_ANON_KEY } from '@/utils/supabaseConfig';

export const loginUser = async (email: string, password: string) => {
  try {
    console.log('Attempting login with Supabase');
    
    // Use fetch directly to debug the request
    const response = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        email: email,
        password: password,
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      console.error('Login response error:', errorData);
      throw new Error(errorData.message || 'Failed to authenticate');
    }
    
    const data = await response.json();
    
    // Set the session manually to ensure it's stored
    const { data: sessionData } = await supabase.auth.setSession({
      access_token: data.access_token,
      refresh_token: data.refresh_token,
    });
    
    return { data: sessionData, error: null };
  } catch (error: any) {
    console.error('Login error in auth service:', error);
    return { data: null, error: error.message };
  }
};

export const signupUser = async (username: string, email: string, password: string) => {
  try {
    // First, sign up the user with email and password
    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email: email,
      password: password,
      options: {
        data: {
          username: username,
        },
      },
    });
    
    if (signUpError) {
      throw signUpError;
    }
    
    // After successful signup, update the user's profile with the username
    // But don't throw an error if this fails - we just log it and continue
    if (signUpData.user) {
      try {
        const { error: profileError } = await supabase
          .from('profiles')
          .insert({
            id: signUpData.user.id,
            username: username
          });
        
        if (profileError) {
          // Important: We're logging the error but NOT throwing it anymore
          // This allows the user to continue even if the profile creation fails
        }
      } catch (profileErr) {
        // Just log the error but don't throw it
      }
    }
    
    return { data: signUpData, error: null };
  } catch (error: any) {
    return { data: null, error: error.message };
  }
};

export const logoutUser = async () => {
  try {
    // Check if there's a valid session first
    const { data: sessionData } = await supabase.auth.getSession();
    
    // Only attempt to sign out if there's an active session
    if (sessionData?.session) {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        throw error;
      }
    }
    
    return { error: null };
  } catch (error: any) {
    return { error: error.message };
  }
};

export const checkUsernameAvailability = async (username: string): Promise<boolean> => {
  try {
    // Use the check_username_exists RPC function instead of querying the table directly
    // This prevents the infinite recursion in RLS policies
    const { data, error } = await supabase
      .rpc('check_username_exists', { username_to_check: username });
    
    if (error) {
      throw error;
    }
    
    // Return the inverse of the result since we want to know if it's available (not exists)
    return !data;
  } catch (error: any) {
    throw error;
  }
};

export const upgradeUserAccount = async (userId: string) => {
  try {
    // Calculate the proExpiresAt timestamp (7 days from now)
    const proExpiresAt = new Date();
    proExpiresAt.setDate(proExpiresAt.getDate() + 7);
    
    const { data, error } = await supabase
      .from('profiles')
      .update({ 
        is_pro: true, 
        pro_expires_at: proExpiresAt.toISOString() 
      })
      .eq('id', userId);
    
    if (error) {
      throw error;
    }
    
    return { data, error: null };
  } catch (error: any) {
    return { data: null, error: error.message };
  }
};

// Add the activateProByUsername function
export const activateProByUsername = async (username: string, daysValid: number = 7): Promise<{ data: any, error: string | null }> => {
  try {
    const { data, error } = await supabase.functions.invoke('activate-pro-by-username', {
      body: { username, daysValid }
    });
    
    if (error) {
      throw error;
    }
    
    return { data, error: null };
  } catch (error: any) {
    return { data: null, error: error.message };
  }
};
